import React, { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { ChevronDown, Check, X, Search } from "lucide-react";
import { updateSegmentMappings } from "../api/costingSettingsApi";

export default function SegmentCategoryMappingSection({
  segmentCategoryMappings = { bulk: [], retail: [], privatelabel: [] },
  availableCategories = [],
  ensureSettingsWriteAccess,
  onSettingsUpdated
}) {
  const [mappings, setMappings] = useState({
    bulk: [],
    retail: [],
    privatelabel: []
  });
  const [saving, setSaving] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (segmentCategoryMappings) {
      setMappings({
        bulk: Array.isArray(segmentCategoryMappings.bulk) ? segmentCategoryMappings.bulk : [],
        retail: Array.isArray(segmentCategoryMappings.retail) ? segmentCategoryMappings.retail : [],
        privatelabel: Array.isArray(segmentCategoryMappings.privatelabel) ? segmentCategoryMappings.privatelabel : []
      });
    }
  }, [segmentCategoryMappings]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
        setSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleCategory = (segment, category) => {
    setMappings((prev) => {
      const currentSegmentList = prev[segment] || [];
      if (currentSegmentList.includes(category)) {
        return {
          ...prev,
          [segment]: currentSegmentList.filter((c) => c !== category)
        };
      } else {
        const newMappings = { ...prev };
        Object.keys(newMappings).forEach((key) => {
          if (key !== segment) {
            newMappings[key] = (newMappings[key] || []).filter((c) => c !== category);
          }
        });
        return {
          ...newMappings,
          [segment]: [...(newMappings[segment] || []), category]
        };
      }
    });
  };

  const removeCategory = (segment, category, e) => {
    e.stopPropagation();
    setMappings((prev) => ({
      ...prev,
      [segment]: prev[segment].filter((c) => c !== category)
    }));
  };

  const handleSave = async () => {
    if (!ensureSettingsWriteAccess("edit segment category mappings")) return;

    setSaving(true);
    try {
      await updateSegmentMappings(mappings);
      toast.success("Segment category mappings updated successfully!");
      if (onSettingsUpdated) {
        onSettingsUpdated();
      }
    } catch (error) {
      toast.error("Failed to update segment category mappings.");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const renderSegmentGroup = (segmentId, title) => {
    const selectedCount = (mappings[segmentId] || []).length;
    const isOpen = openDropdown === segmentId;

    return (
      <div className="flex flex-col gap-2">
        <label className="text-sm font-semibold text-gray-700">{title} Segment</label>
        
        <div className="relative" ref={isOpen ? dropdownRef : null}>
          {/* Dropdown Trigger */}
          <div
            onClick={() => {
              if (isOpen) {
                setOpenDropdown(null);
                setSearchQuery("");
              } else {
                setOpenDropdown(segmentId);
                setSearchQuery("");
              }
            }}
            className={`flex items-center justify-between p-3 bg-white border rounded-xl cursor-pointer transition-all duration-200 ${
              isOpen ? "border-slate-800 ring-1 ring-slate-800" : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <div className="flex flex-wrap gap-2 items-center min-h-[24px]">
              {selectedCount === 0 ? (
                <span className="text-gray-400 text-sm">Select categories...</span>
              ) : (
                <>
                  {(mappings[segmentId] || []).slice(0, 3).map(cat => (
                    <span key={cat} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-100 text-slate-700 text-xs font-medium">
                      {cat}
                      <button onClick={(e) => removeCategory(segmentId, cat, e)} className="hover:text-slate-900 transition-colors">
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                  {selectedCount > 3 && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-50 text-slate-500 text-xs font-medium border border-slate-100">
                      +{selectedCount - 3} more
                    </span>
                  )}
                </>
              )}
            </div>
            <ChevronDown size={16} className={`text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
          </div>

          {/* Dropdown Menu */}
          {isOpen && (
            <div className="absolute top-full left-0 mt-2 w-full max-h-72 flex flex-col bg-white border border-gray-200 shadow-xl rounded-xl z-50 p-2">
              <div className="px-2 pb-2 mb-2 border-b border-gray-100 flex items-center gap-2 sticky top-0 bg-white z-10 pt-1">
                <Search size={16} className="text-gray-400" />
                <input 
                  type="text" 
                  placeholder="Search categories..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full text-sm outline-none border-none focus:ring-0 p-0 text-slate-700 placeholder:text-gray-400 bg-transparent"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="overflow-y-auto custom-scrollbar">
              {(() => {
                const categoriesInOtherSegments = new Set();
                Object.keys(mappings).forEach(key => {
                  if (key !== segmentId) {
                    (mappings[key] || []).forEach(c => categoriesInOtherSegments.add(c));
                  }
                });
                
                const displayableCategories = availableCategories.filter(c => !categoriesInOtherSegments.has(c));
                const filteredCategories = displayableCategories.filter(c => c.toLowerCase().includes(searchQuery.toLowerCase()));

                if (filteredCategories.length === 0) {
                  return <div className="p-3 text-sm text-gray-500 text-center">No categories found</div>;
                }

                return (
                  <>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        const currentSelected = mappings[segmentId] || [];
                        const allFilteredSelected = filteredCategories.length > 0 && filteredCategories.every(c => currentSelected.includes(c));
                        
                        if (allFilteredSelected) {
                          setMappings(prev => ({
                            ...prev,
                            [segmentId]: prev[segmentId].filter(c => !filteredCategories.includes(c))
                          }));
                        } else {
                          setMappings(prev => {
                            const newMappings = { ...prev };
                            const newSegmentList = new Set(newMappings[segmentId] || []);
                            filteredCategories.forEach(c => newSegmentList.add(c));
                            newMappings[segmentId] = Array.from(newSegmentList);
                            return newMappings;
                          });
                        }
                      }}
                      className="flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors border-b border-gray-100 mb-1 hover:bg-gray-50"
                    >
                    <input
                      type="checkbox"
                      checked={(mappings[segmentId] || []).length > 0 && filteredCategories.every(c => (mappings[segmentId] || []).includes(c))}
                      readOnly
                      className="rounded border-gray-300 text-slate-800 focus:ring-slate-800 cursor-pointer w-4 h-4"
                    />
                    <span className="text-sm font-semibold text-slate-800">Select All (Filtered)</span>
                  </div>
                  {filteredCategories.map((cat) => {
                    const isSelected = (mappings[segmentId] || []).includes(cat);
                    return (
                      <div
                        key={cat}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCategory(segmentId, cat);
                        }}
                        className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
                          isSelected ? "bg-slate-50" : "hover:bg-gray-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                          className="rounded border-gray-300 text-slate-800 focus:ring-slate-800 cursor-pointer w-4 h-4"
                        />
                        <span className={`text-sm ${isSelected ? "text-slate-800 font-medium" : "text-gray-600"}`}>
                          {cat}
                        </span>
                      </div>
                    );
                  })}
                </>
                );
              })()}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {renderSegmentGroup("retail", "Retail")}
          {renderSegmentGroup("bulk", "Bulk")}
          {renderSegmentGroup("privatelabel", "Private Label")}
        </div>

        <div className="mt-8 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-2.5 rounded-lg font-medium text-sm transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              "Save Mappings"
            )}
          </button>
        </div>
      </div>
  );
}
